import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Phone, Globe, Search } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import OptimizedImage from '../components/OptimizedImage';
import { getCollegeListingSeoSettings } from '../lib/seoManager';
import { collegeService } from '../lib/database';
import type { College } from '../lib/supabase';
import toast from 'react-hot-toast';

const StreamColleges = () => {
  const { stream } = useParams();
  const [selectedState, setSelectedState] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);

  const states = [
    'all',
    'Andhra Pradesh',
    'Arunachal Pradesh', 
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry'
  ];
  const sortOptions = [
    { value: 'rating', label: 'Rating' },
    { value: 'fees-low', label: 'Fees: Low to High' },
    { value: 'fees-high', label: 'Fees: High to Low' },
    { value: 'established', label: 'Established Year' }
  ];

  // Stream information mapping
  const streamInfo: { [key: string]: any } = {
    'engineering': {
      name: 'Engineering',
      description: 'Explore top engineering colleges across India offering B.Tech, M.Tech, and specialized engineering programs with excellent placement records.',
      icon: '⚙️',
      color: 'from-blue-500 to-blue-600'
    },
    'medical': {
      name: 'Medical',
      description: 'Discover premier medical colleges offering MBBS, MD, MS, and other medical programs with world-class clinical facilities.',
      icon: '🏥',
      color: 'from-red-500 to-red-600'
    },
    'management': {
      name: 'Management',
      description: 'Find top MBA and management institutes with excellent industry connections and placement opportunities.',
      icon: '💼',
      color: 'from-purple-500 to-purple-600'
    },
    'law': {
      name: 'Law',
      description: 'Discover leading law colleges offering LLB, LLM, and specialized legal programs.',
      icon: '⚖️',
      color: 'from-green-500 to-green-600'
    },
    'arts': {
      name: 'Arts & Humanities',
      description: 'Explore colleges offering liberal arts, humanities, and creative programs.',
      icon: '🎨',
      color: 'from-pink-500 to-pink-600'
    },
    'commerce': {
      name: 'Commerce',
      description: 'Find top commerce colleges offering B.Com, M.Com, and business studies.',
      icon: '📊',
      color: 'from-indigo-500 to-indigo-600'
    }
  };

  useEffect(() => {
    fetchColleges();
  }, [stream, selectedState]);

  const fetchColleges = async () => {
    if (!stream) return;
    
    try {
      setLoading(true);
      
      // Convert URL parameter to proper stream name
      const streamName = stream.charAt(0).toUpperCase() + stream.slice(1);
      
      const filters: any = { category: streamName };
      if (selectedState !== 'all') {
        filters.state = selectedState;
      }
      
      const data = await collegeService.getAll(filters);
      setColleges(data);
      
      if (data.length === 0) {
        toast.info(`No colleges found for ${streamName}${selectedState !== 'all' ? ` in ${selectedState}` : ''}`);
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to load colleges');
      setColleges([]);
    } finally {
      setLoading(false);
    }
  };

  const currentStream = streamInfo[stream as string] || {
    name: stream?.charAt(0).toUpperCase() + stream?.slice(1) || 'Unknown',
    description: 'Explore colleges in this field.',
    icon: '🎓',
    color: 'from-blue-500 to-blue-600'
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading {currentStream.name} colleges...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <SEOHead
        title={`Best ${currentStream.name} Colleges in India - Reviews & Ratings | Grad Vikalp`}
        description={`Find the best ${currentStream.name} colleges in India. Compare fees, placements, facilities, and read verified student reviews. Make informed decisions about your ${currentStream.name.toLowerCase()} education.`}
        keywords={`${currentStream.name.toLowerCase()} colleges, top ${currentStream.name.toLowerCase()} colleges, best ${currentStream.name.toLowerCase()} institutes, ${currentStream.name.toLowerCase()} education, ${currentStream.name.toLowerCase()} courses, college reviews, college ratings`}
        url={`/colleges/stream/${stream}`}
        type="website"
      />
      <div className="container py-5">
        {/* Back Button */}
        <Link
          to="/"
          className="btn btn-link text-primary text-decoration-none mb-4 p-0"
        >
          <ArrowLeft className="me-2" size={16} />
          Back to Home
        </Link>

        {/* Stream Header */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="bg-blue-gradient p-5 text-white">
            <div className="d-flex align-items-center gap-3 mb-3">
              <div style={{ fontSize: '2.5rem' }}>{currentStream.icon}</div>
              <div>
                <h1 className="display-5 fw-bold mb-1">{currentStream.name} Colleges</h1>
                <p className="h5 opacity-90 mb-0">{colleges.length} colleges found</p>
              </div>
            </div>
          </div>

          <div className="card-body p-4">
            <p className="text-muted mb-0">{currentStream.description}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold text-dark">
                  Filter by State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="form-select form-select-lg"
                >
                  {states.map(state => (
                    <option key={state} value={state}>
                      {state === 'all' ? 'All States' : state}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold text-dark">
                  Sort by
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-select form-select-lg"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-muted">
            Showing {colleges.length} {currentStream.name.toLowerCase()} college{colleges.length !== 1 ? 's' : ''}
            {selectedState !== 'all' && ` in ${selectedState}`}
          </p>
        </div>

        {/* Colleges Grid */}
        <div className="row g-4">
          {colleges.map((college) => (
            <div key={college.id} className="col-lg-6">
              <div className="card border-0 shadow-lg h-100 college-card">
                <div className="row g-0 h-100">
                  <div className="col-md-5">
                    <OptimizedImage
                      src={college.image_url}
                      alt={`${college.name} - ${college.category} college in ${college.location}, ${college.state}`}
                      title={`${college.name} | ${college.type} ${college.category} College | Rating: ${college.rating}/5`}
                      className="img-fluid h-100 w-100"
                      style={{ objectFit: 'cover', minHeight: '250px' }}
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="col-md-7">
                    <div className="card-body p-4 h-100 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h3 className="h5 fw-bold text-dark mb-1">{college.name}</h3>
                          <div className="d-flex align-items-center gap-3 text-muted small">
                            <div className="d-flex align-items-center gap-1">
                              <MapPin size={14} />
                              <span>{college.location}, {college.state}</span>
                            </div>
                            <span className="badge bg-primary">{college.type}</span>
                          </div>
                        </div>
                        
                        <div className="d-flex align-items-center gap-1">
                          <Star className="star-rating" size={16} fill="currentColor" />
                          <span className="fw-semibold">{college.rating}</span>
                        </div>
                      </div>

                      <p className="text-muted small mb-3 line-clamp-2">
                        {college.description}
                      </p>

                      <div className="mb-3">
                        <h4 className="small fw-semibold text-dark mb-2">Specializations:</h4>
                        <div className="d-flex flex-wrap gap-1">
                          {college.specializations?.slice(0, 3).map((spec) => (
                            <span
                              key={spec}
                              className="badge bg-light text-dark small"
                            >
                              {spec}
                            </span>
                          ))}
                          {college.specializations && college.specializations.length > 3 && (
                            <span className="badge bg-light text-dark small">
                              +{college.specializations.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="row g-2 text-small mb-3">
                        <div className="col-6">
                          <span className="text-muted">Fees:</span>
                          <div className="fw-semibold text-dark small">{college.fees}</div>
                        </div>
                        <div className="col-6">
                          <span className="text-muted">Established:</span>
                          <div className="fw-semibold text-dark small">{college.established}</div>
                        </div>
                      </div>

                      <div className="mt-auto">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-3 text-muted small">
                            {college.contact_phone && (
                              <div className="d-flex align-items-center gap-1">
                                <Phone size={12} />
                                <span style={{ fontSize: '0.75rem' }}>{college.contact_phone}</span>
                              </div>
                            )}
                            {college.contact_website && (
                              <a
                                href={college.contact_website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-flex align-items-center gap-1 text-primary text-decoration-none"
                              >
                                <Globe size={12} />
                                <span style={{ fontSize: '0.75rem' }}>Website</span>
                              </a>
                            )}
                          </div>
                          
                          <Link
                            to={`/college/${college.id}`}
                            className="text-primary fw-semibold text-decoration-none small"
                          >
                            View Details →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {colleges.length === 0 && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <Search className="text-muted mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No colleges found</h3>
                <p className="text-muted mb-4">
                  No colleges found for {currentStream.name} 
                  {selectedState !== 'all' && ` in ${selectedState}`}.
                  Try selecting a different state or check back later.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <button 
                    onClick={() => setSelectedState('all')}
                    className="btn btn-primary"
                  >
                    View All States
                  </button>
                  <Link to="/college-reviews" className="btn btn-outline-primary">
                    Browse All Colleges
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamColleges;